import { EndpointDialog } from "@/components/models/EndpointDialog";
import { ModelJobs, SaveBar, type PickerOpen } from "@/components/models/ModelJobs";
import { ModelPicker, type PickerAddOwn, type PickerSpecial } from "@/components/models/ModelParts";
import { firstModelFor, pickerModelsFor } from "@/components/models/picker-models";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { api, type OrgModels, type PurposeDefView } from "@/lib/api";
import { describeModelError } from "@/lib/model-errors";
import {
  acceptsCustom,
  ASK_ADMIN,
  BUILT_IN_ONLY,
  isChatClass,
  jobLabel,
  joinNames,
  modelName,
  OFF_CONSEQUENCE,
  pendingFor,
} from "@/lib/models";
import { Notice } from "@/pages/container-tabs/connections/ConnectionParts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ManageModelsDialog } from "./ManageModels";
import { useInvalidateModels, useOrgModels } from "./use-org-models";

// The org's Models page: the default model for each job, picked from the
// models the org has on. Owners and admins set them, turn models on and off,
// and add their own models (Manage models); each Boo's admins can pick
// something else for their Boo. Boos that follow a default switch within a
// minute, with no restart; a Boo's own picks are never touched.

type Draft = Record<string, string>;

const FOLLOW = "";

function sameDraft(a: Draft, b: Draft): boolean {
  const norm = (d: Draft) => JSON.stringify(Object.entries(d).filter(([, v]) => v).sort());
  return norm(a) === norm(b);
}

export function OrgModelsTab() {
  const query = useOrgModels();
  if (!query.orgId) return null;
  if (query.isLoading) return <LoadingIndicator />;
  if (query.isError || !query.data) return <ErrorComponent error={query.error ?? "Failed to load models"} />;
  return <OrgModelsEditor data={query.data} orgId={query.orgId} onRetry={() => void query.refetch()} />;
}

function OrgModelsEditor({ data, orgId, onRetry }: { data: OrgModels; orgId: string; onRetry: () => void }) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateModels();
  const canManage = data._permissions.canManage;
  const saved: Draft = data.defaults;
  const [draft, setDraft] = useState<Draft>(saved);
  const [managing, setManaging] = useState(false);
  // The job whose dropdown is open when opened from elsewhere.
  const [openJob, setOpenJob] = useState<string | null>(null);
  // The job whose dropdown opened "Add your own model".
  const [addingFor, setAddingFor] = useState<PurposeDefView | null>(null);
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });

  // ?job=<id> (from "Change" on a Boo's Manage models) opens that dropdown.
  useEffect(() => {
    const job = typeof search.job === "string" ? search.job : null;
    if (!job || !data.purposeDefs.some((d) => d.id === job)) return;
    const t = setTimeout(() => setOpenJob(job), 150);
    const url = new URL(window.location.href);
    url.searchParams.delete("job");
    window.history.replaceState(window.history.state, "", url);
    return () => clearTimeout(t);
  }, [search.job, data.purposeDefs]);

  const dirty = !sameDraft(draft, saved);
  const hasOtherDefaults = Object.entries(draft).some(([k, v]) => k !== "chat" && v);
  const problem =
    hasOtherDefaults && !draft.chat
      ? "Other jobs have their own model, so Conversations needs a specific model too. Pick one above."
      : null;
  const isCustomSlug = (slug: string | undefined) => !!slug && data.endpoints.some((e) => slug.startsWith(`${e.name}/`));

  const save = useMutation({
    mutationFn: () => {
      // Cleared defaults are sent as null so the server drops them.
      const defaults: Record<string, string | null> = { ...draft };
      for (const k of Object.keys(saved)) if (!draft[k]) defaults[k] = null;
      return api.models.setOrg({ defaults });
    },
    onSuccess: () => {
      toast.success("Saved. Boos that follow the default switch within a minute.");
      void invalidate();
    },
    onError: (err) => toast.error(describeModelError(err, { defs: data.purposeDefs, isCustom: isCustomSlug })),
  });

  const setDefault = (purpose: string, value: string) =>
    setDraft((d) => {
      const next = { ...d };
      if (value) next[purpose] = value;
      else delete next[purpose];
      return next;
    });

  const offSet = new Set(data.disabledModels);
  const builtIn = (def: PurposeDefView): PickerSpecial => {
    const off = !!def.defaultModel && offSet.has(def.defaultModel);
    return {
      value: FOLLOW,
      label: "Built-in default",
      detail: def.defaultModel ? `${modelName(def.defaultModel)}${off ? " (turned off)" : ""}` : undefined,
      disabled: off,
    };
  };
  const specialsFor = (def: PurposeDefView): PickerSpecial[] =>
    isChatClass(def) && def.id !== "chat" ? [{ value: FOLLOW, label: "Same as Conversations" }] : [builtIn(def)];

  const addOwnFor = (def: PurposeDefView): PickerAddOwn =>
    !acceptsCustom(def) ? { hint: BUILT_IN_ONLY } : canManage ? { onSelect: () => setAddingFor(def) } : { hint: ASK_ADMIN };

  const picker = (def: PurposeDefView, big: boolean, open: PickerOpen) => (
    <ModelPicker
      ariaLabel={`Default model for ${jobLabel(def)}`}
      value={draft[def.id] ?? FOLLOW}
      onChange={(v) => setDefault(def.id, v)}
      models={pickerModelsFor({ purpose: def, rows: data.models, endpoints: data.endpoints, current: draft[def.id] })}
      specials={specialsFor(def)}
      disabled={!canManage}
      addOwn={addOwnFor(def)}
      className={big ? "h-11" : undefined}
      open={open.open}
      onOpenChange={open.onOpenChange}
    />
  );

  // One notice for Boos that haven't updated, naming only what matters now.
  const noEndpoints = pendingFor(data.pendingBoos, "org_endpoints");
  const noDisabled = pendingFor(data.pendingBoos, "disabled_models");
  const ownMatters = data.endpoints.length > 0 || Object.values(draft).some(isCustomSlug);
  const offMatters = data.disabledModels.length > 0;
  const waiting = [...new Set([...(ownMatters ? noEndpoints : []), ...(offMatters ? noDisabled : [])])];
  const effects = [
    ownMatters && noEndpoints.length > 0 && "can't use your own models",
    offMatters && noDisabled.length > 0 && "may still use models you turned off",
  ].filter(Boolean);
  const specialtyOff = data.purposeDefs.filter((d) => OFF_CONSEQUENCE[d.id]).map((d) => jobLabel(d).toLowerCase());

  const notices = (
    <>
      {data.catalogSource === null && (
        <Notice tone="warning" action={<Button size="sm" variant="outline" onClick={onRetry}>Try again</Button>}>
          <p className="font-medium">The list of models isn&apos;t available right now.</p>
          <p className="text-muted-foreground">Try again in a minute.</p>
        </Notice>
      )}
      {waiting.length > 0 && (
        <Notice tone="info">
          <p className="font-medium">Waiting for an update: {joinNames(waiting, 4)}.</p>
          <p className="text-muted-foreground">
            Until {waiting.length === 1 ? "it updates" : "they update"} (automatically, within a few days){" "}
            {waiting.length === 1 ? "it" : "they"} {joinNames(effects as string[])}.
          </p>
        </Notice>
      )}
    </>
  );

  return (
    <>
      <ModelJobs
        managedBy={canManage ? null : "Managed by your organization admins"}
        onManage={canManage ? () => setManaging(true) : undefined}
        notices={notices}
        jobs={data.purposeDefs}
        picker={picker}
        shownByDefault={(def) => !!draft[def.id] || !!saved[def.id] || !isChatClass(def)}
        openJob={openJob}
        onOpenJobChange={setOpenJob}
        othersNote={specialtyOff.length ? `Each Boo's admins can also turn off ${joinNames(specialtyOff)} for their Boo.` : undefined}
      >
        {canManage && dirty && (
          <SaveBar
            error={problem}
            detail="Boos that follow the default switch within a minute."
            saving={save.isPending}
            onDiscard={() => setDraft(saved)}
            onSave={() => save.mutate()}
          />
        )}
      </ModelJobs>

      <ManageModelsDialog
        open={managing}
        onOpenChange={setManaging}
        onChooseFor={(job) => {
          setManaging(false);
          // After the dialog hands focus back, or the list closes again.
          setTimeout(() => setOpenJob(job), 300);
        }}
      />

      <EndpointDialog
        open={addingFor !== null}
        onOpenChange={(o) => !o && setAddingFor(null)}
        initialRole={addingFor?.role}
        takenNames={data.endpoints.map((e) => e.name)}
        reservedNames={data.reservedEndpointNames}
        purposeDefs={data.purposeDefs}
        onSaved={async (endpoint) => {
          const purpose = addingFor;
          setAddingFor(null);
          // Wait for the new models to be listed, then pick one where the
          // flow started.
          await queryClient.refetchQueries({ queryKey: ["org-models", orgId] });
          void invalidate();
          const slug = purpose && firstModelFor(endpoint, purpose);
          if (purpose && slug) setDefault(purpose.id, slug);
        }}
      />
    </>
  );
}
